import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import { UNITS } from "@/data/site";
import privacy from "@/data/privacy.json";
import cookies from "@/data/cookies.json";

const Footer = () => {
  const { t, lang } = useLang();

  return (
    <footer className="bg-ink text-cream mt-24 md:mt-32" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10">
        <div className="grid md:grid-cols-12 gap-12 md:gap-8 pb-16 md:pb-24">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <p className="font-display italic text-3xl md:text-4xl">Postumia Place</p>
            </div>
            <p className="text-cream/60 leading-relaxed max-w-sm font-light" data-testid="footer-tagline">
              {t.footer.tagline}
            </p>
            <div className="flex items-start gap-3 mt-8 text-cream/60">
              <MapPin className="h-4 w-4 mt-1 shrink-0 text-terracotta-light" />
              <p className="text-sm" data-testid="footer-address">{t.footer.address}</p>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-terracotta-light font-semibold mb-6">{t.footer.contacts}</p>
            <ul className="space-y-4 text-cream/80">
              <li>
                <a href={`mailto:${t.footer.email}`} data-testid="footer-email-link" className="flex items-center gap-3 py-1.5 hover:text-terracotta-light transition-colors duration-200">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="text-sm">{t.footer.email}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${t.footer.phone.replace(/\s/g, "")}`} data-testid="footer-phone-link" className="flex items-center gap-3 py-1.5 hover:text-terracotta-light transition-colors duration-200">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span className="text-sm">{t.footer.phone}</span>
                </a>
              </li>
              <li>
                <a href={`https://wa.me/${t.footer.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-3 py-1.5 hover:text-terracotta-light transition-colors duration-200">
                  <MessageCircle className="h-4 w-4 shrink-0" />
                  <span className="text-sm">WhatsApp {t.footer.whatsapp}</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-terracotta-light font-semibold mb-6">{t.footer.explore}</p>
            <ul className="space-y-3 text-sm text-cream/70">
              <li><Link to="/unita" data-testid="footer-link-unita" className="inline-block py-1.5 hover:text-terracotta-light transition-colors duration-200">{t.nav.units}</Link></li>
              <li><Link to="/dintorni" data-testid="footer-link-dintorni" className="inline-block py-1.5 hover:text-terracotta-light transition-colors duration-200">{t.nav.surroundings}</Link></li>
              <li><Link to="/dove-siamo" data-testid="footer-link-dove-siamo" className="inline-block py-1.5 hover:text-terracotta-light transition-colors duration-200">{t.nav.where}</Link></li>
              <li><Link to="/contatti" data-testid="footer-link-contatti" className="inline-block py-1.5 hover:text-terracotta-light transition-colors duration-200">{t.nav.contact}</Link></li>
              <li><Link to="/prenota" data-testid="footer-link-prenota" className="inline-block py-1.5 hover:text-terracotta-light transition-colors duration-200">{t.nav.book}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-terracotta-light font-semibold mb-6">{t.footer.unitsTitle}</p>
            <ul className="space-y-3 text-sm text-cream/70">
              {UNITS.map((u) => (
                <li key={u.slug}>
                  <Link to={u.path} data-testid={`footer-link-${u.slug}`} className="inline-block py-1.5 hover:text-terracotta-light transition-colors duration-200">
                    {u.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="overflow-hidden select-none" aria-hidden="true">
          <p className="font-display italic whitespace-nowrap leading-[0.85] text-[11vw] xl:text-[8rem] text-outline">
            Postumia Place
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-between gap-3 pt-8 border-t border-cream/10 text-xs text-cream/60">
          <p data-testid="footer-copyright">
            © 2026 Postumia Place · {t.footer.rights} ·{" "}
            <Link to="/privacy" data-testid="footer-privacy-link" className="inline-block py-2 -my-2 underline underline-offset-2 hover:text-terracotta-light">{privacy[lang].link}</Link>{" "}·{" "}
            <Link to="/cookie" data-testid="footer-cookie-link" className="inline-block py-2 -my-2 underline underline-offset-2 hover:text-terracotta-light">{cookies[lang].link}</Link>
          </p>
          <p data-testid="footer-cin">{t.footer.cin}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
